import { afterActionPaint } from '../../shared/after-action-paint';
import { observeActionComputed, unchangedActionComputed, type ActionComputedDelta, type ComputedMember, type ComputedWritePlan } from '../compile/action-computed-delta';
import { SELF_STATUS_SCHEMA, type ActionTruthCommand } from '../actions/self-status';
/** Movement truth commit. Gameplay resolution must be complete before calling this.
 * Transport adapters own authorization, ordering, retry and reconciliation; PHP
 * persists the supplied result but never recalculates movement.
 */
export interface MovementTruthRow {
  encounter_creatures: Record<string, unknown> & { movement_rev?: number };
  position?: Record<string, unknown>;
  /** The row is a whole caster blob (statuses, computed, catalog ...); actions read those buckets. */
  [key: string]: unknown;
}

export interface MovementTruthChange {
  x_pos: number;
  y_pos: number;
  z_pos: number;
  movement_pct?: number;
  aps?: number;
  rev?: number;
}

export interface MovementTruthCommand {
  /** Page-decided GM-only disclosures saved atomically with a solid movement. */
  dispatch_skips?: string[];
  wet?: import('./water-wet.js').WaterWetChange;
  schema: 'janus.movement.truth.v1';
  operation_id: string;
  encounter_id: string;
  actor_id: string;
  expected_rev: number;
  before: MovementTruthChange;
  updates: MovementTruthChange;
  route: Array<[number, number, number]>;
  rev?: number;
  position?: MovementTruthChange;
  /** The page's teleport landing and displacement; native damage rides status_plan in the same commit. */
  teleport?: { landing: { x: number; y: number; z: number }; displaced_ft: number; dice: number; clears?: Array<'grappled' | 'restrained' | 'prone'> };
  /** The page's squeeze state at rest, persisted by the solid receiver. */
  squeeze?: { squeezed: boolean; very: boolean };
  rest?: { hunkered: boolean; squeeze: { squeezed: boolean; very: boolean } | null };
  /** Endpoint statuses resolved before local apply, retained with the immutable save. */
  status_plan?: import('../statuses/gm-boundary.js').GmBoundaryVerdict;
  /** The browser's Burrowed transition, saved atomically with the position. */
  burrow?: { transition: 'enter' | 'leave' | 'none' };
  /** Browser-decided compound members; PHP compares revisions and saves the rows together. A creature member carries exactly
   *  the receiver's columns (id, expected_rev, before, after): a follower's end-of-move Squeezed state is never a member column —
   *  it rides the same native `status_plan` as the mover's (dispatch-skips.ts movementStatusInputs20). */
  compound?: { members: Array<({ id: string; expected_rev: number; inventory_id?: never } | { inventory_id: string; expected_row_revision?: string; id?: never; expected_rev?: never })
    & { before: { x_pos: number; y_pos: number; z_pos: number }; after: { x_pos: number; y_pos: number; z_pos: number } }> };
  /** Planning-only, never posted (post() omits it): each compound follower whose Squeezed / Very Squeezed state CHANGES at its
   *  end-of-move point, by member id. prepare() decides it once and the outbox freezes it with the command, so the settlement travels
   *  with the operation through every transport retry and rides the same native `status_plan` as the mover's
   *  (dispatch-skips.ts movementStatusInputs20). */
  follower_squeezes?: Record<string, { squeezed: boolean; very: boolean }>;
  follower_hunkers?: string[];
  /** Janus session 3 step 4: the walk was halted by a trap — the host, the verb, the firing foot and the browser-resolved fire verdict. */
  trap?: { host_inventory_id: string; verb: string; foot: { x: number; y: number }; fire: Record<string, unknown> };
  /** MV6: browser-resolved opposing-body contests, recorded atomically with this walk. */
  overrun?: import('./overrun-contest.js').OverrunEvidence;
  /** MV11: derived climb face verdict, recorded atomically with the settled walk. */
  climb?: import('./climb-walk.js').ClimbEvidence;
  /** MV8 (R-008 stealth walk): the awareness contests / reveals the page decided on this walk (ft_stealth_record.php records them). */
  stealth?: import('./stealth-walk.js').StealthWalkEvidence;
  /** 2026-10-07 (held-target-move.ts): a force move of a creature the CASTER holds — the receivers authorize the caster and prove the hold
   *  from the status row (held_target_move_authority.php) instead of the moved row's own permission. */
  held_move?: { caster_id: string; origin_id: string };
}

export interface MovementTruthAcceptedResult {
  ok: true;
  applied: boolean;
  replay?: boolean;
  operation_id?: string;
  position: MovementTruthChange;
  rev: number;
}

export interface MovementTruthRejectedResult {
  ok: false;
  error: 'stale';
  position: MovementTruthChange;
  rev: number;
}

export class MovementTruthRejected extends Error {
  constructor(readonly result: MovementTruthRejectedResult) {
    super(result.error);
    this.name = 'MovementTruthRejected';
  }
}

/** A definitive non-stale refusal (400/403/409-reused/410). Retrying the same bytes cannot
 * succeed, so the outbox must not hold the actor's queue behind it. */
export class MovementTruthTerminal extends Error {
  /** `body`: the refusal's answer body (R-121's uniform `{reason, error, rev, authoritative}`), when the lane parsed one. */
  constructor(readonly status: number, message: string, readonly body: Record<string, unknown> | null = null) {
    super(message);
    this.name = 'MovementTruthTerminal';
  }
}

export interface MovementTruthTerminalInfo {
  error: string;
  status: number;
  terminal: true;
  /** True when this entry was invalidated because an EARLIER entry in the chain failed. */
  chained?: boolean;
  /** The refusal's answer body, when the lane passed it (R-121: its `reason` is the server's contract word). */
  body?: Record<string, unknown> | null;
}

export function isMovementTruthAcceptedResult(value: unknown): value is MovementTruthAcceptedResult {
  const result = value as Partial<MovementTruthAcceptedResult> | null;
  // Movement answers carry `position`; an action answer carries `frame` (A1). Either is an
  // accepted commit for the shared outbox; each schema's accepted() reads its own payload.
  return result?.ok === true && typeof result.applied === 'boolean'
    && Number.isInteger(result.rev) && result.rev! >= 0
    && (typeof result.position === 'object' || typeof (result as { frame?: unknown }).frame === 'object');
}

/** Write the current-AP scalar the way the row builder stores it, with its DERIVED companion `aps_used`
 * (`build_encounter_creature_arr`: `max(0, aps_total − aps)`, elided when 0; `aps_total` floors at 3 on a
 * compiled row). ONE rule shared by movement (M1c) and actions (A1) so the two lanes cannot drift; pass an
 * explicit companion only when authority supplied one (an A1 frame carries it on the wire). */
export function applyApsScalar(ec: Record<string, unknown>, aps: number, apsUsed?: number | null): void {
  ec.aps = aps;
  const used = apsUsed === undefined
    ? Math.max(0, Number(Array.isArray(ec.aps_total) ? ec.aps_total[0] : ec.aps_total ?? 3) - aps)
    : (apsUsed ?? 0);
  if (used > 0) ec.aps_used = used; else delete ec.aps_used;
}

export function applyMovementTruth(row: MovementTruthRow, change: MovementTruthChange | MovementTruthCommand): void {
  const command: MovementTruthCommand | null = 'updates' in change ? change as MovementTruthCommand : null;
  const updates: MovementTruthChange = command ? command.position ?? command.updates : change as MovementTruthChange;
  const rev = command?.rev ?? updates.rev;
  // Keep row and bucket identity: the encounter store and caster cache share them.
  row.position ??= {};
  for (const key of ['x_pos', 'y_pos', 'z_pos'] as const) {
    row.position[key] = updates[key];
  }
  // Coordinates belong only to position. A revision-only/A1 reconciliation must also
  // preserve the unchanged bank's compiled shape (PDO DECIMAL string, or elided zero):
  // the C1 fingerprint includes this bucket. Real bank changes still apply normally.
  if (updates.movement_pct !== undefined) {
    const stored = row.encounter_creatures.movement_pct;
    const current = Array.isArray(stored) ? stored[0] : stored;
    const numeric = current === undefined ? 0
      : (typeof current === 'number' || (typeof current === 'string' && current.trim() !== '')) ? Number(current) : NaN;
    if (!Number.isFinite(numeric) || numeric !== updates.movement_pct) row.encounter_creatures.movement_pct = updates.movement_pct;
  }
  // M1c: an AP scalar always carries its derived companion (a walk that banked an AP changes both).
  if (updates.aps !== undefined) applyApsScalar(row.encounter_creatures, updates.aps);
  if (Number.isInteger(rev) && rev! >= 0) row.encounter_creatures.movement_rev = rev;
}

/** Apply only a newer committed movement frame. Used by the WS adapter's ordering gate. */
export function applyMovementTruthIfNewer(row: MovementTruthRow, command: MovementTruthCommand): boolean {
  if (!Number.isInteger(command.rev) || command.rev! < 0) return false;
  const current = Number(row.encounter_creatures.movement_rev ?? 0);
  if (Number.isFinite(current) && current >= command.rev!) return false;
  applyMovementTruth(row, command);
  return true;
}

/** Any per-actor client-truth command (movement or an action) the shared outbox can carry: the
 * identity + revision contract is common; `before` names the row scalars the command was resolved
 * from; `updates`/`route` are movement's. Actions carry their own payload (see
 * janus-engine/actions/self-status.ts) and supply their `apply` to commitMovementTruth. */
export interface ActorTruthCommandBase {
  schema: string;
  operation_id: string;
  encounter_id: string;
  actor_id: string;
  expected_rev: number;
  before: Partial<Record<'x_pos' | 'y_pos' | 'z_pos' | 'movement_pct' | 'aps', number>>;
  updates?: MovementTruthChange;
  route?: Array<[number, number, number]> | number[][];
}

export function commitMovementTruth<C extends ActorTruthCommandBase = MovementTruthCommand>(input: {
  command: C;
  row: MovementTruthRow;
  render: (command: C, row: MovementTruthRow) => void;
  onRenderError: (error: unknown, command: C, row: MovementTruthRow) => void;
  commit: (command: C) => Promise<unknown>;
  /** Optimistic in-place mutation for a NON-movement schema (A1 actions). Movement's default applies
   * `updates` at expected_rev + 1. */
  apply?: (row: MovementTruthRow, command: C, optimisticRev: number) => void;
  /** The lane's existing participant list, including secondary/hidden members. */
  computedMembers?: Iterable<ComputedMember>;
  /** The writer declares a saved-row-only apply, even when using a schema callback. */
  computedUnchanged?: boolean;
  /** The custom writer's branches, declared before optimistic apply. */
  computedWritePlan?: ReadonlyMap<string, ComputedWritePlan>;
  /** Expose the local result before render events can ask U0 for its frame. */
  onComputed?: (computed: ActionComputedDelta) => void;
}): { commit: Promise<unknown>; computed: ActionComputedDelta } {
  // Optimistic revision: a move queued behind this one is resolved from this destination, so it
  // must carry the revision the server will hold once this commit applies. A stale rejection
  // invalidates the whole chain behind it (outbox), never a silently re-based later move.
  const onComputed = input.onComputed;
  input.onComputed = computed => {
    try { onComputed?.(computed); }
    catch (error) { try { input.onRenderError(error, input.command, input.row); } catch { /* observer only */ } }
  };
  let members: Iterable<ComputedMember> = input.computedMembers ?? [[input.command.actor_id, input.row] as const];
  let plans = input.computedWritePlan;
  if (!plans && input.apply && !input.computedUnchanged && input.command.schema === SELF_STATUS_SCHEMA) {
    const frame = (input.command as unknown as ActionTruthCommand).frame;
    const held = Array.from(members); members = held;
    plans = new Map(held.map(([id]) => [id.toUpperCase(), id.toUpperCase() === input.command.actor_id.toUpperCase()
      ? { computed: Object.keys(frame.skills ?? {}).map(key => ['creature', 'skills', key]), statuses: !!frame.statuses_target_entry } : {}]));
  } else if (!plans && input.apply && !input.computedUnchanged && input.command.schema === 'janus.movement.truth.v1') {
    const wet = (input.command as unknown as MovementTruthCommand).wet;
    const held = Array.from(members); members = held;
    plans = new Map(held.map(([id]) => [id.toUpperCase(), { statuses: !!wet }]));
  }
  // Saved-only movement stays free. Other action schemas still require their
  // own writer plans; preserve their existing discovery until they are migrated.
  const finishComputed = input.apply && !input.computedUnchanged ? observeActionComputed(members, plans) : () => unchangedActionComputed(members);
  let computed: ActionComputedDelta;
  try {
    if (input.apply) input.apply(input.row, input.command, input.command.expected_rev + 1);
    else applyMovementTruth(input.row, { ...(input.command.updates as MovementTruthChange), rev: input.command.expected_rev + 1 });
  } finally { computed = finishComputed(); }
  input.onComputed?.(computed);
  // Keep transport outside the local apply/paint task.
  const commit = afterActionPaint(() => input.commit(input.command));
  try {
    input.render(input.command, input.row);
  } catch (error) {
    try { input.onRenderError(error, input.command, input.row); } catch { /* observer only */ }
  }
  return { commit, computed };
}
